import { Injectable } from '@angular/core';
import {
  FormGroup,
  FormControl,
  AbstractControl,
  ValidatorFn,
  Validators,
} from '@angular/forms';

/** バリデーション用デバイス種類のViewModel */
export type DeviceTypeValidatorViewModel = {
  /** デバイス種類ID */
  deviceTypeId?: string;
  /** 説明 */
  description?: string;
  // センサーデータ/クラウドデータのデータ受信はnumber[]で受け取るがデバイス新規登録or編集ダイアログではstring型としてデータを受け取る、
  /** センサーデータ収集間隔選択肢(CSV形式)*/
  samplingFrequency?: number[];
  /** クラウドへのデータ送信間隔選択肢(CSV形式) */
  reportingFrequency?: number[];
  /** 温度センサー */
  temperatureSensor?: boolean;
  /** 気圧センサー */
  pressureSensor?: boolean;
  /** 湿度センサー */
  humiditySensor?: boolean;
  /** 傾斜センサー */
  tiltSensor?: boolean;
  /** 照度センサー */
  lightSensor?: boolean;
  /** 位置即位 */
  locationSensor?: boolean;
  /** 位置即位技術(GNSS) */
  locationTechGnss?: boolean;
  /** 位置即位技術(Wi-Fi) */
  locationTechWifi?: boolean;
  /** 位置即位技術(Cellular) */
  locationTechCellular?: boolean;
  /** 落下検知 */
  fallDetected?: boolean;
  /** 動作検知 */
  mcdDetected?: boolean;
  /** 明暗検知 */
  lightExposureDetected?: boolean;
  /** 衝撃検知 */
  shockDetected?: boolean;
  /** 温度センサー */
  temperature?: boolean;
  /** 気圧センサー */
  pressure?: boolean;
  /** 湿度センサー */
  humidity?: boolean;
  /** 傾斜センサー */
  tilt?: boolean;
  /** 照度センサー */
  light?: boolean;
  /** 位置測位 */
  locationDetected?: boolean;
  /** フライトモード */
  fmd?: boolean;
};

export type QTSFormGroup = FormGroup<{
  /** センサーデータ収集期間 */
  samplingFrequency: FormControl<number | null>;
  /** クラウドデータ収集期間 */
  reportingFrequency: FormControl<number | null>;
  /** 位置検知 */
  locationDetected: FormControl<boolean>;
  /** 位置測位技術優先度 */
  locationPriority: FormGroup<{
    locationPriority1: FormControl<string>;
    locationPriority2: FormControl<string>;
    locationPriority3: FormControl<string>;
  }>;
  /** 落下検知 */
  fallDetected: FormControl<boolean>;
  /** 動作検知 */
  mcdDetected: FormControl<boolean>;
  /** 明暗検知 */
  lightExposureDetected: FormControl<boolean>;
  /** 明暗検知設定値 */
  lightExposureDetectValue: FormControl<string | null>;
  /** 衝撃検知 */
  shockDetected: FormControl<boolean>;
  /** 衝撃検知設定値 */
  shockDetectedDetectValue: FormControl<number | null>;
  /** 温度センサー */
  temperature: FormControl<boolean>;
  /** 温度センサー設定 */
  temperatureSettings: FormGroup<{
    /** 温度センサー：上限逸脱 */
    temperatureAlertMax: FormControl<number | null>;
    /** 温度センサー：上限警告 */
    temperaturePrealertMax: FormControl<number | null>;
    /** 温度センサー：下限警告 */
    temperaturePrealertMin: FormControl<number | null>;
    /** 温度センサー：下限逸脱 */
    temperatureAlertMin: FormControl<number | null>;
  }>;
  /** 気圧センサー */
  pressure: FormControl<boolean>;
  /** 気圧センサー設定 */
  pressureSettings: FormGroup<{
    /** 気圧センサー：上限逸脱 */
    pressureAlertMax: FormControl<number | null>;
    /** 気圧センサー：上限警告 */
    pressurePrealertMax: FormControl<number | null>;
    /** 気圧センサー：下限警告 */
    pressurePrealertMin: FormControl<number | null>;
    /** 気圧センサー：下限逸脱 */
    pressureAlertMin: FormControl<number | null>;
  }>;
  /** 湿度センサー */
  humidity: FormControl<boolean>;
  /** 湿度センサー設定 */
  humiditySettings: FormGroup<{
    /** 湿度センサー：上限逸脱 */
    humidityAlertMax: FormControl<number | null>;
    /** 湿度センサー：上限警告 */
    humidityPrealertMax: FormControl<number | null>;
    /** 湿度センサー：下限警告 */
    humidityPrealertMin: FormControl<number | null>;
    /** 湿度センサー：下限逸脱 */
    humidityAlertMin: FormControl<number | null>;
  }>;
  /** 傾斜センサー */
  tilt: FormControl<boolean>;
  /** 傾斜センサー設定 */
  tiltSettings: FormGroup<{
    /** 傾斜センサー：上限逸脱 */
    tiltAlertMax: FormControl<number | null>;
  }>;
  /** 照度センサー */
  light: FormControl<boolean>;
  /** 照度センサー設定 */
  lightSettings: FormGroup<{
    /** 照度センサー：上限逸脱 */
    lightAlertMax: FormControl<number | null>;
    /** 照度センサー：上限警告 */
    lightPrealertMax: FormControl<number | null>;
    /** 照度センサー：下限警告 */
    lightPrealertMin: FormControl<number | null>;
    /** 照度センサー：下限逸脱 */
    lightAlertMin: FormControl<number | null>;
  }>;

  /** 配送機能での利用許可 */
  shipmentTracking: FormControl<boolean>;
}>;

export type DeviceModel = {
  /** センサーデータ収集間隔 */
  samplingFrequency?: number | null;
  /** クラウドへのデータ送信間隔 */
  reportingFrequency: number | null;
  /** 配送機能での利用許可 */
  shipmentTracking: boolean;
  /** 位置検知 */
  locationDetected?: boolean;
  /** 位置測位技術優先度 */
  locationPriority?: (string | null)[];
  /** 落下検知イベント */
  fallDetected?: boolean;
  /** 動作検知イベント */
  mcdDetected?: boolean;
  /** 明暗検知イベント */
  lightExposureDetected?: boolean;
  /** 明暗検知イベント設定値 */
  lightExposureDetectValue?: string;
  /** 衝撃検知イベント */
  shockDetected?: boolean;
  /** 衝撃検知イベント設定値 */
  shockDetectedDetectValue?: number;
  /** 温度センサー */
  temperature?: boolean;
  /** 温度設定値 */
  temperatureSettings?: (number | null)[];
  /** 気圧センサー */
  pressure?: boolean;
  /** 気圧設定値 */
  pressureSettings?: (number | null)[];
  /** 湿度センサー */
  humidity?: boolean;
  /** 湿度設定値 */
  humiditySettings?: (number | null)[];
  /** 傾斜センサー */
  tilt?: boolean;
  /** 傾斜設定値 */
  tiltSettings?: (number | null)[];
  /** 照度センサー */
  light?: boolean;
  /** 照度設定値 */
  lightSettings?: (number | null)[];
  /** フライトモード */
  fmd?: boolean;
};

// センサータイプ・スイッチ名 定義
const TEMPERATURE_LABEL = 'temperature';
const PRESSURE_LABEL = 'pressure';
const HUMIDITY_LABEL = 'humidity';
const TILT_LABEL = 'tilt';
const LIGHT_LABEL = 'light';
const SAMPLING_FREQUENCY_LABEL = 'samplingFrequency';
const REPORTING_FREQUENCY_LABEL = 'reportingFrequency';

// FormGroupの設定用語 定義
const SETTING_LABEL = 'Settings';
const ALERT_MAX_LABEL = 'AlertMax';
const ALERT_MIN_LABEL = 'AlertMin';
const PREALERT_MAX_LABEL = 'PrealertMax';
const PREALERT_MIN_LABEL = 'PrealertMin';

@Injectable({
  providedIn: 'root',
})
export class CreateQTSFormGroup {
  /** 測定と送信間隔値 デフォルト[5, 10, 15, 30, 60] */
  public defaultIntervals: number[] = [5, 10, 15, 30, 60];

  /** 位置測位技術優先度 デフォルト */
  public locationPriorities: { [key: string]: string } = {
    GNSS: 'GNSS',
    'Wi-Fi': 'WIFI',
    Cellular: 'CELL',
  };

  /** 明暗検知設定値 デフォルト */
  public defaultLightExposureDetectValues = {
    Continuous: 'continuous',
    'One-time': 'onetime',
  };

  /** 衝撃検知設定値 デフォルト */
  public defaultShockDetectedDetectValues = [5, 10, 15, 20, 25, 30];

  /** 有効な位置測位技術数 */
  private locationPriorityNum: number = 0;

  /** 位置測位有効設定レベル */
  private validLevels = [
    'locationPriority1',
    'locationPriority2',
    'locationPriority3',
  ];

  /**
   * 位置測位技術優先度のリストを更新
   * @param locationTechCellular Cellular
   * @param locationTechGnss GNSS
   * @param locationTechWifi Wi-Fi
   */
  public updateLocationPriorities(
    locationTechCellular: boolean | undefined,
    locationTechGnss: boolean | undefined,
    locationTechWifi: boolean | undefined,
  ) {
    if (!locationTechCellular && this.locationPriorities['Cellular']) {
      delete this.locationPriorities['Cellular'];
    } else if (locationTechCellular) {
      this.locationPriorities['Cellular'] = 'CELL';
    }

    if (!locationTechGnss && this.locationPriorities['GNSS']) {
      delete this.locationPriorities['GNSS'];
    } else if (locationTechGnss) {
      this.locationPriorities['GNSS'] = 'GNSS';
    }

    if (!locationTechWifi && this.locationPriorities['Wi-Fi']) {
      delete this.locationPriorities['Wi-Fi'];
    } else if (locationTechWifi) {
      this.locationPriorities['Wi-Fi'] = 'WIFI';
    }
    return this.locationPriorities;
  }

  /**
   * 位置測位技術優先度の選択値が有効か判定する。
   * @param value 選択した値
   * @param compareValue1 重複を比較する値１
   * @param compareValue2 重複を比較する値２
   * @returns 有効/無効フラグ
   */
  public isLocationPriorityDisabled(
    value: string,
    compareValue1: string | null,
    compareValue2: string | null,
  ) {
    if (!value) {
      return false;
    }

    if (value === compareValue1 || value === compareValue2) {
      return true;
    }
    return false;
  }

  /**
   * 送信間隔の選択肢が選択可能（非活性）か
   *
   * @param samplingFrequency 測定間隔
   * @param reportingFrequency 送信間隔の選択肢
   * @returns trueなら選択不可、falseなら選択可能
   */
  public isDisabledReportingFrequency(
    samplingFrequency: number,
    reportingFrequency: number,
  ) {
    /**
     * 測定間隔が未選択なら、この選択肢（reportingFrequency）は選択可能
     * 測定間隔より小さいか、測定間隔の倍数でなければ、選択不可
     * それ以外は選択可能
     */
    if (samplingFrequency == null) {
      return false;
    } else if (
      reportingFrequency < samplingFrequency ||
      reportingFrequency % samplingFrequency !== 0
    ) {
      return true;
    }
    return false;
  }

  /**
   * フォームコントロールの変更を購読
   */
  public subscribeFormChanges(formGroup: FormGroup) {
    // 初期表示時にdisabledプロパティでは無効化されないため、
    // 明示的にdisabledになるように実装

    // トグルスイッチのON/OFFに合わせて入力可否を切り替える
    formGroup.get('locationDetected')?.valueChanges.subscribe((value) => {
      if (value) {
        formGroup.get('locationPriority')?.enable();
      } else {
        formGroup.get('locationPriority')?.disable();
      }
    });
    formGroup.get('shockDetected')?.valueChanges.subscribe((value) => {
      if (value) {
        formGroup.get('shockDetectedDetectValue')?.enable();
      } else {
        formGroup.get('shockDetectedDetectValue')?.disable();
      }
    });
    formGroup.get('lightExposureDetected')?.valueChanges.subscribe((value) => {
      if (value) {
        formGroup.get('lightExposureDetectValue')?.enable();
      } else {
        formGroup.get('lightExposureDetectValue')?.disable();
      }
    });
    formGroup.get(TEMPERATURE_LABEL)?.valueChanges.subscribe((value) => {
      if (value) {
        formGroup.get('temperatureSettings')?.enable();
      } else {
        formGroup.get('temperatureSettings')?.disable();
      }
    });
    formGroup.get(PRESSURE_LABEL)?.valueChanges.subscribe((value) => {
      if (value) {
        formGroup.get('pressureSettings')?.enable();
      } else {
        formGroup.get('pressureSettings')?.disable();
      }
    });
    formGroup.get(HUMIDITY_LABEL)?.valueChanges.subscribe((value) => {
      if (value) {
        formGroup.get('humiditySettings')?.enable();
      } else {
        formGroup.get('humiditySettings')?.disable();
      }
    });
    formGroup.get(TILT_LABEL)?.valueChanges.subscribe((value) => {
      if (value) {
        formGroup.get('tiltSettings')?.enable();
      } else {
        formGroup.get('tiltSettings')?.disable();
      }
    });
    formGroup.get(LIGHT_LABEL)?.valueChanges.subscribe((value) => {
      if (value) {
        formGroup.get('lightSettings')?.enable();
      } else {
        formGroup.get('lightSettings')?.disable();
      }
    });
  }

  public createQTSFormGroup(
    device?: DeviceModel,
    deviceType?: DeviceTypeValidatorViewModel,
  ) {
    // 有効な位置測位技術数
    const locationPriority = [
      deviceType?.locationTechCellular,
      deviceType?.locationTechGnss,
      deviceType?.locationTechWifi,
    ];

    this.locationPriorityNum += locationPriority.filter(Boolean).length;

    // 設定するバリデーションを指定
    let setValidators: ValidatorFn | undefined;

    if (
      (!device && !deviceType) ||
      !(
        deviceType?.deviceTypeId === 'QTS112' ||
        deviceType?.deviceTypeId === 'QTS110'
      )
    ) {
      setValidators = this.otherValidator(deviceType);
    } else if (deviceType?.deviceTypeId === 'QTS110') {
      setValidators = this.qts110Validator(deviceType);
    } else if (deviceType?.deviceTypeId === 'QTS112') {
      setValidators = this.qts112Validator(deviceType);
    }

    const location = device?.locationDetected;
    const shock = device?.shockDetected;
    const fallDetected = device?.fallDetected;
    const mcdDetected = device?.mcdDetected;
    const lightExposure = device?.lightExposureDetected;
    const temperature = device?.temperature;
    const humidity = device?.humidity;
    const pressure = device?.pressure;
    const tilt = device?.tilt;
    const light = device?.light;
    const fmd = device?.fmd;

    // 測定間隔の値がセンサーデータ収集間隔選択肢(CSV形式)に含まれているか判定
    let samplingFrequencyValue: number | null = null;
    if (
      deviceType?.samplingFrequency &&
      device?.samplingFrequency !== undefined &&
      device?.samplingFrequency !== null
    ) {
      samplingFrequencyValue = deviceType.samplingFrequency.includes(
        device.samplingFrequency,
      )
        ? device.samplingFrequency
        : null;
    }

    // 送信間隔の値がクラウドへのデータ送信間隔選択肢(CSV形式)に含まれているか判定
    let reportingFrequencyValue: number | null = 0;
    if (deviceType?.reportingFrequency) {
      reportingFrequencyValue = deviceType.reportingFrequency.includes(
        device?.reportingFrequency || 0,
      )
        ? device!.reportingFrequency
        : null;
    }

    // FormControlの値は、デバイス種類で「無」設定されている場合、データがサーバから取れてもnullとし、バリデーションもOFFにする。
    // そうしないと画面表示されないが裏側で値を持っていてバリデーションがかかることになるため。
    const qtsValidatorFormGroup = new FormGroup(
      {
        samplingFrequency: new FormControl<number | null>({
          value:
            // QTS112の場合は測定間隔項目は無効
            deviceType?.deviceTypeId === 'QTS112'
              ? null
              : samplingFrequencyValue,
          disabled: deviceType?.deviceTypeId === 'QTS112',
        }),
        reportingFrequency: new FormControl<number | null>({
          value: reportingFrequencyValue,
          disabled: false,
        }),
        locationDetected: new FormControl<boolean>({
          value: deviceType?.locationSensor ? (location ?? false) : false,
          disabled: false,
        }),
        locationPriority: new FormGroup({
          locationPriority1: new FormControl<string | null>({
            value: device?.locationPriority?.[0] || null,
            disabled: !location || false,
          }),
          locationPriority2: new FormControl<string | null>({
            value: device?.locationPriority?.[1] || null,
            disabled: !location || false,
          }),
          locationPriority3: new FormControl<string | null>({
            value: device?.locationPriority?.[2] || null,
            disabled: !location || false,
          }),
        }),
        shockDetected: new FormControl<boolean>({
          value: deviceType?.shockDetected
            ? shock != null
              ? shock
              : false
            : false,
          disabled: false,
        }),
        shockDetectedDetectValue: new FormControl<number | null>({
          value: device?.shockDetectedDetectValue || null,
          disabled: !shock || false,
        }),
        fallDetected: new FormControl<boolean>({
          value: deviceType?.fallDetected
            ? fallDetected != null
              ? fallDetected
              : false
            : false,
          disabled: false,
        }),
        mcdDetected: new FormControl<boolean>({
          value: deviceType?.mcdDetected
            ? mcdDetected != null
              ? mcdDetected
              : false
            : false,
          disabled: false,
        }),
        lightExposureDetected: new FormControl<boolean>({
          value: deviceType?.lightExposureDetected
            ? lightExposure != null
              ? lightExposure
              : false
            : false,
          disabled: false,
        }),
        lightExposureDetectValue: new FormControl<string | null>({
          value: device?.lightExposureDetectValue?.toLowerCase() || null,
          disabled: !lightExposure || false,
        }),
        temperature: new FormControl<boolean>({
          value:
            deviceType?.temperature || deviceType?.temperatureSensor
              ? temperature != null
                ? temperature
                : false
              : false,
          disabled: false,
        }),
        humidity: new FormControl<boolean>({
          value:
            deviceType?.humidity || deviceType?.humiditySensor
              ? humidity != null
                ? humidity
                : false
              : false,
          disabled: false,
        }),
        pressure: new FormControl<boolean>({
          value:
            deviceType?.pressure || deviceType?.pressureSensor
              ? pressure != null
                ? pressure
                : false
              : false,
          disabled: false,
        }),
        tilt: new FormControl<boolean>({
          value:
            deviceType?.tilt || deviceType?.tiltSensor
              ? tilt != null
                ? tilt
                : false
              : false,
          disabled: false,
        }),
        light: new FormControl<boolean>({
          value:
            deviceType?.light || deviceType?.lightSensor
              ? light != null
                ? light
                : false
              : false,
          disabled: false,
        }),
        temperatureSettings: new FormGroup({
          temperatureAlertMax: new FormControl<number | null>({
            value: device?.temperatureSettings?.[0] ?? null,
            disabled: !temperature || false,
          }),
          temperaturePrealertMax: new FormControl<number | null>({
            value: device?.temperatureSettings?.[1] ?? null,
            disabled: !temperature || false,
          }),
          temperaturePrealertMin: new FormControl<number | null>({
            value: device?.temperatureSettings?.[2] ?? null,
            disabled: !temperature || false,
          }),
          temperatureAlertMin: new FormControl<number | null>({
            value: device?.temperatureSettings?.[3] ?? null,
            disabled: !temperature || false,
          }),
        }),
        pressureSettings: new FormGroup({
          pressureAlertMax: new FormControl<number | null>({
            value: device?.pressureSettings?.[0] ?? null,
            disabled: !pressure || false,
          }),
          pressurePrealertMax: new FormControl<number | null>({
            value: device?.pressureSettings?.[1] ?? null,
            disabled: !pressure || false,
          }),
          pressurePrealertMin: new FormControl<number | null>({
            value: device?.pressureSettings?.[2] ?? null,
            disabled: !pressure || false,
          }),
          pressureAlertMin: new FormControl<number | null>({
            value: device?.pressureSettings?.[3] ?? null,
            disabled: !pressure || false,
          }),
        }),

        humiditySettings: new FormGroup({
          humidityAlertMax: new FormControl<number | null>({
            value: device?.humiditySettings?.[0] ?? null,
            disabled: !humidity || false,
          }),
          humidityPrealertMax: new FormControl<number | null>({
            value: device?.humiditySettings?.[1] ?? null,
            disabled: !humidity || false,
          }),
          humidityPrealertMin: new FormControl<number | null>({
            value: device?.humiditySettings?.[2] ?? null,
            disabled: !humidity || false,
          }),
          humidityAlertMin: new FormControl<number | null>({
            value: device?.humiditySettings?.[3] ?? null,
            disabled: !humidity || false,
          }),
        }),

        tiltSettings: new FormGroup({
          tiltAlertMax: new FormControl<number | null>({
            value: device?.tiltSettings?.[0] ?? null,
            disabled: !tilt || false,
          }),
        }),

        lightSettings: new FormGroup({
          lightAlertMax: new FormControl<number | null>({
            value: device?.lightSettings?.[0] ?? null,
            disabled: !light || false,
          }),
          lightPrealertMax: new FormControl<number | null>({
            value: device?.lightSettings?.[1] ?? null,
            disabled: !light || false,
          }),
          lightPrealertMin: new FormControl<number | null>({
            value: device?.lightSettings?.[2] ?? null,
            disabled: !light || false,
          }),
          lightAlertMin: new FormControl<number | null>({
            value: device?.lightSettings?.[3] ?? null,
            disabled: !light || false,
          }),
        }),

        fmd: new FormControl<boolean>({
          value: deviceType?.fmd ? (fmd != null ? fmd : false) : false,
          disabled: false,
        }),
      },
      {
        validators: setValidators,
      },
    );

    this.markFormGroupTouched(qtsValidatorFormGroup);

    this.formGroupValueChanged(qtsValidatorFormGroup, deviceType);

    return qtsValidatorFormGroup;
  }

  /**
   * QTS110のバリデーション
   * @param deviceType
   * @returns
   */
  private qts110Validator(
    deviceType: DeviceTypeValidatorViewModel,
  ): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      if (!(control instanceof FormGroup)) return null;
      const qtsValues = control.controls;
      // 基本的にデバイス種類で「無」になっているものはバリデーションを実施しない。

      const validLightExposureSelectionValues = ['continuous', 'onetime'];

      // 測定間隔のValidation（デバイス種類による有／無設定は存在しない）
      const isValid = (
        deviceType.samplingFrequency ?? this.defaultIntervals
      ).includes(qtsValues[SAMPLING_FREQUENCY_LABEL].value);

      if (!qtsValues[SAMPLING_FREQUENCY_LABEL].value) {
        qtsValues[SAMPLING_FREQUENCY_LABEL].setErrors({ required: true });
      }

      if (!isValid) {
        qtsValues[SAMPLING_FREQUENCY_LABEL].setErrors({
          invalidSelection: true,
        });
      }

      // 送信間隔のValidation（有／無設定は存在しない）
      // 送信間隔は測定間隔以上、かつ倍数である必要がある。

      if (!qtsValues[REPORTING_FREQUENCY_LABEL].value) {
        qtsValues[REPORTING_FREQUENCY_LABEL].setErrors({ required: true });
      }
      if (
        !(
          (deviceType.reportingFrequency ?? this.defaultIntervals).includes(
            qtsValues[REPORTING_FREQUENCY_LABEL].value,
          ) &&
          qtsValues[REPORTING_FREQUENCY_LABEL].value >=
            qtsValues[SAMPLING_FREQUENCY_LABEL].value &&
          qtsValues[REPORTING_FREQUENCY_LABEL].value %
            qtsValues[SAMPLING_FREQUENCY_LABEL].value ===
            0
        )
      ) {
        qtsValues[REPORTING_FREQUENCY_LABEL].setErrors({
          invalidSelection: true,
        });
      }

      if (!deviceType) {
        return null;
      }

      // 位置情報検知のValidation
      if (
        deviceType.locationSensor === true ||
        deviceType.locationDetected === true ||
        control.get('locationDetected')?.value
      ) {
        const locationList = qtsValues['locationPriority'].value;

        this.locationValidation(control, locationList);
      }

      // 温度のValidation
      if (
        deviceType.temperatureSensor === true ||
        deviceType.temperature === true ||
        control.get(TEMPERATURE_LABEL)?.value
      ) {
        if (
          qtsValues['temperatureSettings'].value['temperatureAlertMax'] > 60
        ) {
          control
            ?.get('temperatureSettings')
            ?.get('temperatureAlertMax')!
            .setErrors({ overMax: true });
        }
        if (
          Number(
            qtsValues['temperatureSettings'].value['temperatureAlertMin'],
          ) < -20
        ) {
          control
            ?.get('temperatureSettings')
            ?.get('temperatureAlertMin')!
            .setErrors({ underMin: true });
        }
      }

      // 気圧のValidation
      if (
        deviceType.pressureSensor === true ||
        deviceType.pressure === true ||
        control.get(PRESSURE_LABEL)?.value
      ) {
        if (qtsValues['pressureSettings'].value['pressureAlertMax'] > 1260) {
          control
            ?.get('pressureSettings')
            ?.get('pressureAlertMax')!
            .setErrors({ overMax: true });
        }
        if (qtsValues['pressureSettings'].value['pressureAlertMin'] < 260) {
          control
            ?.get('pressureSettings')
            ?.get('pressureAlertMin')!
            .setErrors({ underMin: true });
        }
      }

      // 湿度のValidation
      if (
        deviceType.humiditySensor === true ||
        deviceType.humidity === true ||
        control.get(HUMIDITY_LABEL)?.value
      ) {
        if (qtsValues['humiditySettings'].value['humidityAlertMax'] > 100) {
          control
            ?.get('humiditySettings')
            ?.get('humidityAlertMax')!
            .setErrors({ overMax: true });
        }
        if (qtsValues['humiditySettings'].value['humidityAlertMin'] < 0) {
          control
            ?.get('humiditySettings')
            ?.get('humidityAlertMin')!
            .setErrors({ underMin: true });
        }
      }

      // 照度のValidation
      if (
        deviceType.lightSensor === true ||
        deviceType.light === true ||
        control.get(LIGHT_LABEL)?.value
      ) {
        if (qtsValues['lightSettings'].value['lightAlertMax'] > 66000) {
          control
            ?.get('lightSettings')
            ?.get('lightAlertMax')!
            .setErrors({ overMax: true });
        }
        // 暗所の場合常にアラートが発生するため、照度のアラート下限は-1を許容する。
        if (Number(qtsValues['lightSettings'].value['lightAlertMin']) < -1) {
          control
            ?.get('lightSettings')
            ?.get('lightAlertMin')!
            .setErrors({ underMin: true });
        }
      }

      // 傾きのValidation
      if (
        deviceType.tiltSensor === true ||
        deviceType.tilt === true ||
        control.get(TILT_LABEL)?.value
      ) {
        if (qtsValues['tiltSettings'].value['tiltAlertMax'] > 180) {
          control
            ?.get('tiltSettings')
            ?.get('tiltAlertMax')!
            .setErrors({ overMax: true });
        }
      }

      // 衝撃検知のValidation
      if (
        deviceType.shockDetected === true ||
        control.get('shockDetected')?.value
      ) {
        if (
          !this.defaultShockDetectedDetectValues.includes(
            qtsValues['shockDetectedDetectValue'].value,
          ) &&
          qtsValues['shockDetectedDetectValue'].value !== null
        ) {
          qtsValues['shockDetectedDetectValue'].setErrors({
            invalidSelection: true,
          });
        }
      }

      // 明暗のValidation
      if (
        deviceType.lightExposureDetected === true ||
        control.get('lightExposureDetected')?.value
      ) {
        if (
          !validLightExposureSelectionValues.includes(
            qtsValues['lightExposureDetectValue'].value,
          ) &&
          qtsValues['lightExposureDetectValue'].value !== null
        ) {
          qtsValues['lightExposureDetectValue'].setErrors({
            invalidSelection: true,
          });
        }
      }

      this.validateRangeBounds(control);

      this.setRequiredValidation(control, deviceType, false);

      return null;
    };
  }

  /**
   * QTS112のバリデーション
   * @param deviceType
   * @returns
   */
  private qts112Validator(
    deviceType: DeviceTypeValidatorViewModel,
  ): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      if (!(control instanceof FormGroup)) return null;
      const qtsValues = control.controls;

      if (!deviceType) {
        return null;
      }

      // 位置情報検知のValidation;
      if (
        deviceType.locationSensor === true ||
        deviceType.locationDetected === true ||
        control.get('locationDetected')?.value
      ) {
        const locationList = qtsValues['locationPriority'].value;
        Object.entries(locationList).forEach(([key, value]) => {
          if (value === 'GNSS') {
            control
              ?.get('locationPriority')
              ?.get(key)!
              .setErrors({ invalidValue: true });
          }
        });

        this.locationValidation(control, locationList);

        this.validateRangeBounds(control);

        this.setRequiredValidation(control, deviceType, false);
      }
      return null;
    };
  }

  /**
   * その他デバイスのバリデーション
   * @param deviceType
   * @returns
   */
  private otherValidator(
    deviceType?: DeviceTypeValidatorViewModel,
  ): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      if (!(control instanceof FormGroup)) return null;

      if (!deviceType) {
        return null;
      }

      // その他デバイスの測定間隔のバリデーション追加
      control.get('samplingFrequency')?.setValidators(Validators.required);

      this.validateRangeBounds(control);

      this.setRequiredValidation(control, deviceType, true);

      return null;
    };
  }

  /**
   * 上限・下限の数値判定
   * @param control
   */
  private validateRangeBounds(control: FormGroup) {
    // 温度
    this.rangeBoundsSetErrors(control, TEMPERATURE_LABEL);

    // 気圧
    this.rangeBoundsSetErrors(control, PRESSURE_LABEL);

    // 湿度
    this.rangeBoundsSetErrors(control, HUMIDITY_LABEL);

    // 照度
    this.rangeBoundsSetErrors(control, LIGHT_LABEL);

    // 傾斜
    this.rangeBoundsSetErrors(control, TILT_LABEL);
  }

  /**
   * 数値のバリデーション結果によるエラー設定を実行する
   * @param control
   * @param sensorType
   */
  private rangeBoundsSetErrors(control: FormGroup, sensorType: string) {
    const qtsValues = control.controls;
    const pattern = /^-?\d+(\.\d{1,1})?$/;

    const groupName = sensorType + SETTING_LABEL;
    const alertMax = sensorType + ALERT_MAX_LABEL;
    const alertMin = sensorType + ALERT_MIN_LABEL;
    const prealertMax = sensorType + PREALERT_MAX_LABEL;
    const prealertMin = sensorType + PREALERT_MIN_LABEL;

    if (sensorType === TILT_LABEL) {
      if (!pattern.test(qtsValues[groupName].value[alertMax])) {
        control
          ?.get(groupName)
          ?.get(alertMax)!
          .setErrors({ invalidFormat: true });
      } else if (qtsValues[groupName].value[alertMax] < 0) {
        this.resetHasError(control, groupName, alertMax);
        control?.get(groupName)?.get(alertMax)!.setErrors({ underMin: true });
      }
      return;
    }

    if (!pattern.test(qtsValues[groupName].value[alertMax])) {
      control
        ?.get(groupName)
        ?.get(alertMax)!
        .setErrors({ invalidFormat: true });
    }
    if (!pattern.test(qtsValues[groupName].value[alertMin])) {
      control
        ?.get(groupName)
        ?.get(alertMin)!
        .setErrors({ invalidFormat: true });
    }

    if (!pattern.test(qtsValues[groupName].value[prealertMax])) {
      control
        ?.get(groupName)
        ?.get(prealertMax)!
        .setErrors({ invalidFormat: true });
    } else if (
      Number(qtsValues[groupName].value[alertMax]) <
      Number(qtsValues[groupName].value[prealertMax])
    ) {
      control?.get(groupName)?.get(prealertMax)!.setErrors({ overMax: true });
    } else if (
      Number(qtsValues[groupName].value[prealertMax]) <
      Number(qtsValues[groupName].value[prealertMin])
    ) {
      control?.get(groupName)?.get(prealertMax)!.setErrors({ underMin: true });
    } else if (qtsValues[groupName].value[prealertMax]) {
      this.resetHasError(control, groupName, prealertMax);
    }

    if (!pattern.test(qtsValues[groupName].value[prealertMin])) {
      control
        ?.get(groupName)
        ?.get(prealertMin)!
        .setErrors({ invalidFormat: true });
    } else if (
      Number(qtsValues[groupName].value[alertMin]) >
        Number(qtsValues[groupName].value[prealertMin]) &&
      sensorType !== LIGHT_LABEL &&
      Number(qtsValues[groupName].value[alertMin]) !== -1
    ) {
      control?.get(groupName)?.get(prealertMin)!.setErrors({ underMin: true });
    } else if (qtsValues[groupName].value[prealertMax]) {
      this.resetHasError(control, groupName, prealertMin);
    }
  }

  private resetHasError(
    control: FormGroup,
    groupName: string,
    controlName: string,
  ) {
    control?.get(groupName)?.get(controlName)!.setErrors(null);
  }

  /**
   * 必須バリデーター設定する
   * @param control
   * @param deviceType
   */
  private setRequiredValidation(
    control: FormGroup,
    deviceType: DeviceTypeValidatorViewModel,
    isLocationValidation: boolean,
  ) {
    // 位置情報検知のValidation
    if (isLocationValidation) {
      if (
        deviceType.locationSensor === true ||
        deviceType.locationDetected === true ||
        control.get('locationDetected')?.value
      ) {
        // 位置検知トグルスイッチの必須
        control.get('locationDetected')?.setValidators(Validators.required);

        // 位置測位技術優先度の必須
        if (this.locationPriorityNum > 0) {
          control
            .get('locationPriority')
            ?.get('locationPriority1')
            ?.addValidators(Validators.required);
        }
      }
    }

    // 温度のValidation
    if (
      deviceType.temperatureSensor === true ||
      deviceType.temperature === true ||
      control.get(TEMPERATURE_LABEL)?.value
    ) {
      control.get(TEMPERATURE_LABEL)?.addValidators(Validators.required);
    }

    this.requiredSetErrors(control, TEMPERATURE_LABEL);

    // 気圧のValidation
    if (
      deviceType.pressureSensor === true ||
      deviceType.pressure === true ||
      control.get(PRESSURE_LABEL)?.value
    ) {
      control.get(PRESSURE_LABEL)?.addValidators(Validators.required);
    }

    this.requiredSetErrors(control, PRESSURE_LABEL);

    // 湿度のValidation
    if (
      deviceType.humiditySensor === true ||
      deviceType.humidity === true ||
      control.get(HUMIDITY_LABEL)?.value
    ) {
      control.get(HUMIDITY_LABEL)?.addValidators(Validators.required);
    }

    this.requiredSetErrors(control, HUMIDITY_LABEL);

    // 照度のValidation
    if (
      deviceType.lightSensor === true ||
      deviceType.light === true ||
      control.get(LIGHT_LABEL)?.value
    ) {
      control.get(LIGHT_LABEL)?.addValidators(Validators.required);
    }

    this.requiredSetErrors(control, LIGHT_LABEL);

    // 傾きのValidation
    if (
      deviceType.tiltSensor === true ||
      deviceType.tilt === true ||
      control.get(TILT_LABEL)?.value
    ) {
      control.get(TILT_LABEL)?.addValidators(Validators.required);
    }

    this.requiredSetErrors(control, TILT_LABEL);

    // 衝撃検知のValidation
    if (
      deviceType.shockDetected === true ||
      control.get('shockDetected')?.value
    ) {
      control
        .get('shockDetectedDetectValue')
        ?.addValidators(Validators.required);
    }

    // 明暗のValidation
    if (
      deviceType.lightExposureDetected === true ||
      control.get('lightExposureDetected')?.value
    ) {
      control
        .get('lightExposureDetectValue')
        ?.addValidators(Validators.required);
    }

    const light = control.get('light')?.value;
    const lightExposureDetectedForm = control.get('lightExposureDetected');
    const lightExposureDetected = lightExposureDetectedForm?.value;

    if (lightExposureDetected && deviceType.lightSensor) {
      if (!light) {
        lightExposureDetectedForm?.setErrors({ requiredLight: true });
      } else {
        lightExposureDetectedForm?.setErrors(null);
      }
    } else {
      lightExposureDetectedForm?.setErrors(null);
    }
  }

  /**
   * 必須バリデーション結果によるエラー設定を実行する
   * @param control
   * @param sensorType
   */
  private requiredSetErrors(control: FormGroup, sensorType: string) {
    const groupName = sensorType + SETTING_LABEL;
    const alertMax = sensorType + ALERT_MAX_LABEL;
    const alertMin = sensorType + ALERT_MIN_LABEL;
    const prealertMax = sensorType + PREALERT_MAX_LABEL;
    const prealertMin = sensorType + PREALERT_MIN_LABEL;

    if (control.get(sensorType)) {
      control.get(groupName)?.get(alertMax)?.addValidators(Validators.required);

      if (sensorType !== TILT_LABEL) {
        control
          .get(groupName)
          ?.get(prealertMax)
          ?.addValidators(Validators.required);
        control
          .get(groupName)
          ?.get(prealertMin)
          ?.addValidators(Validators.required);
        control
          .get(groupName)
          ?.get(alertMin)
          ?.addValidators(Validators.required);
      }
    }
  }

  /**
   * 位置測位技術優先度バリデーター設定
   * @param control
   * @param locationList
   */
  private locationValidation(control: FormGroup, locationList: string[]) {
    const selectedLevels = Object.entries(locationList)
      .filter(
        ([key, value]) => value !== null && this.validLevels.includes(key),
      )
      .map(([key]) => key);

    // 1つ以上のレベルが選択されていることを確認
    if (selectedLevels.length === 0) {
      control?.get('locationPriority')?.get('locationPriority1')!.setErrors({
        invalidSelection: true,
      });
      control
        ?.get('locationPriority')
        ?.get('locationPriority2')!
        .setErrors(null);
      control
        ?.get('locationPriority')
        ?.get('locationPriority3')!
        .setErrors(null);
    } else {
      // 許可されている組み合わせを確認
      const allowedCombinations = [
        ['locationPriority1'],
        ['locationPriority1', 'locationPriority2'],
        ['locationPriority1', 'locationPriority2', 'locationPriority3'],
      ];

      const isValidCombination = allowedCombinations.some(
        (combination) =>
          combination.length === selectedLevels.length &&
          combination.every((level) => selectedLevels.includes(level)),
      );

      if (!isValidCombination) {
        // 位置測位優先度技術1と3のみ選択している場合
        if (
          selectedLevels[0] === this.validLevels[0] &&
          selectedLevels[1] === this.validLevels[2]
        ) {
          control
            ?.get('locationPriority')
            ?.get('locationPriority2')!
            .setErrors({
              invalidSelection: true,
            });
        }
        // 位置測位優先度技術2のみ選択している場合
        else if (selectedLevels[0] === this.validLevels[1]) {
          control
            ?.get('locationPriority')
            ?.get('locationPriority1')!
            .setErrors({
              invalidSelection: true,
            });
        }
        // 位置測位優先度技術3のみ選択している場合
        else if (selectedLevels[0] === this.validLevels[2]) {
          control
            ?.get('locationPriority')
            ?.get('locationPriority1')!
            .setErrors({
              invalidSelection: true,
            });
          control
            ?.get('locationPriority')
            ?.get('locationPriority2')!
            .setErrors({
              invalidSelection: true,
            });
        }
      }
    }
  }

  /**
   * FormControlのtouchedプロパティをtrueに設定
   * @param formGroup
   */
  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach((control) => {
      if (control instanceof FormControl) {
        control.markAsTouched();
      } else if (control instanceof FormGroup) {
        // 再帰的にネストされたFormGroupも処理
        this.markFormGroupTouched(control);
      }
    });
  }

  /**
   * 測定間隔で選択された値と同じ値を送信間隔に自動で選択する。
   */
  private formGroupValueChanged(
    formGroup: FormGroup,
    deviceType?: DeviceTypeValidatorViewModel,
  ) {
    formGroup.controls['samplingFrequency'].valueChanges.subscribe(
      (samplingFrequency) => {
        const reportingFrequencyFormControl =
          formGroup.controls['reportingFrequency'];

        // 送信間隔が測定間隔と比較して適切な値が入っている場合、フォームグループに何も変化を加えない。
        if (
          !this.isDisabledReportingFrequency(
            samplingFrequency,
            reportingFrequencyFormControl.value,
          )
        ) {
          return;
        }

        if (!samplingFrequency) {
          reportingFrequencyFormControl.setValue(null);
        } else if (
          deviceType?.samplingFrequency ??
          this.defaultIntervals.includes(samplingFrequency)
        ) {
          reportingFrequencyFormControl.setValue(samplingFrequency);
        } else {
          const filteredReportingFrequency = (
            deviceType?.samplingFrequency ?? this.defaultIntervals
          ).filter(
            (reportingFrequency) =>
              reportingFrequency >= samplingFrequency &&
              reportingFrequency % samplingFrequency === 0,
          );
          if (filteredReportingFrequency.length > 0) {
            reportingFrequencyFormControl.setValue(
              filteredReportingFrequency[0],
            );
          } else {
            reportingFrequencyFormControl.setValue(null);
          }
        }
      },
    );
  }
}
